/**
 * CUSTOMER-R4.15-E — lazy PDP merchandising without full catalog download.
 * Main PDP renders first; secondary modules fetch related category shards only.
 */
import type { CatalogV2Product } from "./catalog-v2-types.js";
import { loadCategoryPage } from "./catalog-v2-data.js";
import {
  getProductRecommendations,
  merchPoolCategoryIdsForProduct,
  type ProductRecommendationItem,
} from "./product-recommendations-v2.js";
import { readRecentProductShells } from "./recent-products-v2.js";
import type { StoreV2Lang } from "./store-v2-locale.js";
import { fetchOnsiteRecommendations, mapOnsiteToCrossSell } from "./onsite-recommendations-v2.js";

export type PdpMerchResult = {
  crossSell: ProductRecommendationItem[];
  recommended: ProductRecommendationItem[];
  recentlyViewed: CatalogV2Product[];
};

async function loadMerchPool(product: CatalogV2Product): Promise<CatalogV2Product[]> {
  const cats = merchPoolCategoryIdsForProduct(product, 3);
  const pages = await Promise.all(
    cats.map(async (categoryId) => {
      try {
        return await loadCategoryPage(categoryId, 1, "recommended");
      } catch {
        return null;
      }
    }),
  );
  const byId = new Map<string, CatalogV2Product>();
  for (const page of pages) {
    if (!page) continue;
    for (const p of page.products) {
      if (!byId.has(p.id)) byId.set(p.id, p);
    }
  }
  return [...byId.values()];
}

export async function loadPdpMerchandising(
  product: CatalogV2Product,
  lang: StoreV2Lang = "ua",
  catalogById?: Map<string, CatalogV2Product>,
): Promise<PdpMerchResult> {
  const onsite = await fetchOnsiteRecommendations({
    surface: "PRODUCT_CROSS_SELL",
    anchorProductId: product.id,
    limit: 4,
  }).catch(() => null);

  const pool = await loadMerchPool(product);
  let crossSell: ProductRecommendationItem[] = [];

  if (onsite?.ok && onsite.enabled && !onsite.hidden && onsite.items?.length) {
    const byId = catalogById ?? new Map(pool.map((p) => [p.id, p]));
    crossSell = mapOnsiteToCrossSell(onsite.items, byId, lang).map((c, idx) => ({
      ...c,
      placement: "PDP_CROSS_SELL" as const,
      strategy: "cross_sell_rules" as const,
      rank: idx + 1,
    }));
  }

  if (crossSell.length === 0) {
    crossSell = getProductRecommendations({
      productId: product.id,
      product,
      catalog: pool,
      lang,
      placement: "PDP_CROSS_SELL",
      max: 3,
    });
  }
  const exclude = new Set(crossSell.map((c) => c.product.id));
  exclude.add(product.id);
  const recommended = getProductRecommendations({
    productId: product.id,
    product,
    catalog: pool,
    lang,
    placement: "PDP_RECOMMENDED",
    max: 3,
    excludeIds: exclude,
  });
  const recentlyViewed = readRecentProductShells()
    .filter((p) => p.id !== product.id)
    .filter((p) => p.isSellable !== false)
    .filter((p) => p.stockStatus !== "out_of_stock" && p.stockStatus !== "deleted")
    .slice(0, 6);
  return { crossSell, recommended, recentlyViewed };
}
